import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Button, Form, Card, Alert } from 'react-bootstrap';
import { Auth_Proto, Auth_Domain, Auth_Port } from '../../Component/consts';
import Header from '../../Component/Header';
import Menu from '../../Component/Menu';
import Footer from '../../Component/Footer';
import { Link } from 'react-router-dom';
import { Autocomplete, TextField } from '@mui/material';
import NewJournalService from '../NewJournalService';
import PlComponent from '../reports/PlComponent';
import { financialyearstart, financialyearend, currentTenant, getFinancialYearLabel } from '../../Common/Credentials';

const ProfitDistributionWizard = () => {
    const [netProfit, setNetProfit] = useState('');
    const [retainedEarnings, setRetainedEarnings] = useState('');
    const [financialYearId, setFinancialYearId] = useState('');
    const [fylist, setFylist] = useState([]);
    const [isLoss, setIsLoss] = useState(false);
    const [result, setResult] = useState(null);
    const tenantId = currentTenant; // || 'DEFAULT_TENANT'

    useEffect(() => {
        NewJournalService.getFinancialYears(tenantId).then(res => {
            const def = { from: financialyearstart, to: financialyearend };
            const rawList = [...(res.data || []), def];
            const uniqueList = [];
            const seen = new Set();
            rawList.forEach((item) => {
                if (!item || !item.from || !item.to) return;
                const lbl = getFinancialYearLabel(item.from, item.to);
                if (!seen.has(lbl)) {
                    seen.add(lbl);
                    uniqueList.push(item);
                }
            });
            setFylist(uniqueList);
        }).catch(err => console.error("Error fetching financial years:", err));
    }, [tenantId]);

    const getFyId = (from, to, id) => {
        const lbl = getFinancialYearLabel(from, to);
        if (lbl) return lbl;
        return id ? String(id) : 'current';
    };

    const handleExecute = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${Auth_Proto}${Auth_Domain}${Auth_Port}/api/accounts/execute`, null, {
                params: {
                    tenantId,
                    financialYearId,
                    netProfit: parseFloat(netProfit || 0),
                    retainedEarnings: parseFloat(retainedEarnings || 0)
                }
            });
            setResult(res.data);
            alert("Distribution Executed Successfully!");
        } catch (err) {
            console.error("Error executing distribution:", err);
            alert(err.response?.data || "Failed to execute distribution.");
        }
    };

    return (
        <div>
            <Header />
            <Menu />
            <div className="content-wrapper">
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Profit Distribution Wizard</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item">
                                        <Link to="/accounts" className="btn btn-danger btn-sm text-white">
                                            <i className="fas fa-times"></i> Close
                                        </Link>
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="content">
                    <div className="container-fluid">
                        <Card className="p-3 mb-4">
                            <Form onSubmit={handleExecute}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Financial Year</Form.Label>
                                    <Autocomplete 
                                        options={fylist}
                                        getOptionLabel={(option) => {
                                            if (option && option.from && option.to) {
                                                return getFinancialYearLabel(option.from, option.to);
                                            }
                                            return 'Current Year';
                                        }}
                                        onChange={async (event, newValue) => {
                                            if (newValue) {
                                                const fyId = getFyId(newValue.from, newValue.to, newValue.id);
                                                setFinancialYearId(fyId);
                                                const val = await PlComponent.onFySelect(newValue);
                                                if (val.debitexcess > 0) {
                                                    setNetProfit(val.debitexcess);
                                                    setIsLoss(false);
                                                } else if (val.creditexcess > 0) {
                                                    const lossVal = -val.creditexcess;
                                                    setNetProfit(lossVal);
                                                    setIsLoss(true);
                                                    setRetainedEarnings(lossVal);
                                                } else {
                                                    setNetProfit(0);
                                                    setIsLoss(false);
                                                }

                                                // Fetch existing saved distribution record for this FY
                                                try {
                                                    const existingRecordRes = await axios.get(`${Auth_Proto}${Auth_Domain}${Auth_Port}/api/accounts/record/${tenantId}/${fyId}`);
                                                    if (existingRecordRes.data && (existingRecordRes.data.id || existingRecordRes.data.netProfit !== undefined)) {
                                                        const rec = existingRecordRes.data;
                                                        if (rec.retainedEarningsAmount !== null && rec.retainedEarningsAmount !== undefined) {
                                                            setRetainedEarnings(rec.retainedEarningsAmount);
                                                        }
                                                        setResult(rec);
                                                    } else {
                                                        setResult(null);
                                                    }
                                                } catch (e) {
                                                    setResult(null);
                                                }
                                            } else {
                                                setFinancialYearId('');
                                                setNetProfit('');
                                                setRetainedEarnings('');
                                                setIsLoss(false);
                                                setResult(null);
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select Financial Year" required />}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Net Profit / Loss (Calculated from P&L)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.01" 
                                        required 
                                        value={netProfit} 
                                        readOnly
                                        onChange={e => setNetProfit(e.target.value)} 
                                        placeholder="Auto-calculated from P&L"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Requested Retained Earnings</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.01" 
                                        value={retainedEarnings} 
                                        disabled={isLoss}
                                        onChange={e => setRetainedEarnings(e.target.value)} 
                                        placeholder="0.00"
                                    />
                                    <Form.Text className="text-muted">
                                        {isLoss ? "When there is a loss, retained amount is automatically equal to the loss and deducted from equity in the Balance Sheet." : "Enter the amount to retain in company reserves."}
                                    </Form.Text>
                                </Form.Group>
                                <Button 
                                    className="btn hovbuttonColour text-white" 
                                    type="submit" 
                                    disabled={!financialYearId || netProfit === ''}
                                >
                                    Execute Distribution
                                </Button>
                            </Form>
                        </Card>

                        {result && (
                            result.netProfit < 0 ? (
                                <Alert variant="warning">
                                    <h4>Net Loss Retained Earnings Breakdown</h4>
                                    <p><strong>Net Loss for the Year:</strong> {result.netProfit}</p>
                                    <p><strong>Statutory Reserve:</strong> 0.00</p>
                                    <p><strong>Retained Loss (Deducted from Equity):</strong> <span className="text-danger font-weight-bold">{result.retainedEarningsAmount || result.netProfit}</span></p>
                                    <p className="mb-0 text-muted">The full loss is retained and will appear as a deduction under <strong>Retained earnings</strong> in the Balance Sheet.</p>
                                </Alert>
                            ) : (
                                <Alert variant="success">
                                    <h4>Profit Distribution Breakdown</h4>
                                    <p><strong>Net Profit:</strong> {result.netProfit}</p>
                                    <p><strong>Statutory Reserve Deducted:</strong> {result.statutoryReserveAmount}</p>
                                    <p><strong>Retained Earnings:</strong> {result.retainedEarningsAmount}</p>
                                    <p><strong>Total Distributed to Shareholders:</strong> {result.totalDistributedAmount}</p>
                                    {result.partnerAllocations ? (
                                        <>
                                            <h5 className="mt-3">Partner Profit Allocations (By Shareholding %)</h5>
                                            <table className="table table-bordered table-sm mt-2 bg-white">
                                                <thead>
                                                    <tr>
                                                        <th>Partner Name</th>
                                                        <th>Shareholding (%)</th>
                                                        <th>Allocated Profit Share</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        let allocs = [];
                                                        try {
                                                            allocs = typeof result.partnerAllocations === 'string' 
                                                                ? JSON.parse(result.partnerAllocations) 
                                                                : result.partnerAllocations;
                                                        } catch (e) { allocs = []; }
                                                        return (allocs || []).map((p, idx) => (
                                                            <tr key={p.partnerId || idx}>
                                                                <td>{p.partnerName}</td>
                                                                <td>{p.sharePercentage}%</td>
                                                                <td className="text-success font-weight-bold">{p.allocatedAmount}</td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </table>
                                        </>
                                    ) : null}
                                </Alert>
                            )
                        )}
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default ProfitDistributionWizard;
